import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useSnackbar } from 'notistack'

import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import InputAdornment from '@mui/material/InputAdornment'
import LoadingButton from '@mui/lab/LoadingButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import ErrorIcon from '@mui/icons-material/Error'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PrintIcon from '@mui/icons-material/Print'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import TextInputController from '@/components/ui/inputs/TextInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'

import {
  BillingIssueReason,
  BillingReviewStatus,
  Consultation,
  DesmosActStatus,
  PaymentType,
} from '@/core/models/consultation.model'
import { Document, objectTypes } from '@/core/models/document.model'
import {
  useUpdateBillingReviewMutation,
  useUpdateConsultationMutation,
} from '@/core/services/consultation'
import { useAddFileMutation } from '@/core/services/file'

// ─────────────────────────────────────────────────────────────────────────────
// Hook — manages dialog state. Opening is idempotent; auto-open is gated by
// the caller using `billingReview.status` so the dialog keeps re-surfacing
// until the user explicitly marks billing as completed or reports an issue.
// ─────────────────────────────────────────────────────────────────────────────

export function useConsultationPaymentDialog() {
  const [open, setOpen] = useState(false)
  const [consultationId, setConsultationId] = useState<string | null>(null)
  // Tracks which consultation IDs the user has manually dismissed in this
  // session — prevents the effect-driven auto-open from immediately reopening
  // after a manual close, while still allowing explicit showDialog() calls
  // and future navigations to bring it back.
  const [dismissedInSession] = useState(() => new Set<string>())

  const showDialog = (id: string) => {
    dismissedInSession.delete(id)
    setConsultationId(id)
    setOpen(true)
  }

  const autoShowDialog = (id: string) => {
    if (dismissedInSession.has(id)) return
    setConsultationId(id)
    setOpen(true)
  }

  const hideDialog = () => {
    if (consultationId) dismissedInSession.add(consultationId)
    setOpen(false)
  }

  return { open, consultationId, showDialog, autoShowDialog, hideDialog }
}

// ─────────────────────────────────────────────────────────────────────────────
// FSP file detection
// ─────────────────────────────────────────────────────────────────────────────

/**
 * An FSP (Feuille de Soins Papier) file is identifiable by its filename: both
 * the auto-generated path (`fsp_auto_...pdf`) and the manual dialog output
 * (`fsp_<timestamp>_<name>_<surname>.pdf`) share the `fsp_` prefix.
 */
function findFspFile(files?: Document[]): Document | undefined {
  if (!files?.length) return undefined
  const fspFiles = files.filter((f) => f.filename?.toLowerCase().startsWith('fsp_'))
  if (fspFiles.length === 0) return undefined
  // Pick the most recent by filename (timestamps are embedded in the name)
  return fspFiles.reduce((latest, curr) => (curr.filename > latest.filename ? curr : latest))
}

// ─────────────────────────────────────────────────────────────────────────────
// Section header — numbered step for visual flow
// ─────────────────────────────────────────────────────────────────────────────

function SectionHeader({ step, title, status }: { step: number; title: string; status?: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
      <Avatar
        sx={{
          width: 28,
          height: 28,
          fontSize: '0.85rem',
          fontWeight: 700,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        {step}
      </Avatar>
      <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
        {title}
      </Typography>
      {status}
    </Stack>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Desmos status chip
// ─────────────────────────────────────────────────────────────────────────────

function DesmosStatusChip({ desmosActStatus }: { desmosActStatus?: DesmosActStatus }) {
  const { t } = useTranslation()

  if (!desmosActStatus) {
    return (
      <Chip
        icon={<HourglassEmptyIcon />}
        label={t('page.liveConsultation.payment.desmos.status.notProcessed', { defaultValue: 'Non traité' })}
        color="default"
        size="small"
        variant="outlined"
      />
    )
  }

  const { success, acts = [] } = desmosActStatus
  const failedActs = acts.filter((a) => !a.success)

  if (success && failedActs.length === 0) {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label={t('page.liveConsultation.payment.desmos.status.success', { defaultValue: 'Envoyé à Desmos' })}
        color="success"
        size="small"
        variant="filled"
      />
    )
  }

  if (failedActs.length > 0) {
    return (
      <Chip
        icon={<WarningAmberIcon />}
        label={t('page.liveConsultation.payment.desmos.status.partial', { defaultValue: '{{count}} acte(s) en erreur', count: failedActs.length })}
        color="warning"
        size="small"
        variant="filled"
      />
    )
  }

  return (
    <Chip
      icon={<ErrorIcon />}
      label={t('page.liveConsultation.payment.desmos.status.error', { defaultValue: 'Échec' })}
      color="error"
      size="small"
      variant="filled"
    />
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Desmos act status section
// ─────────────────────────────────────────────────────────────────────────────

function DesmosActStatusSection({ desmosActStatus }: { desmosActStatus?: DesmosActStatus }) {
  const { t } = useTranslation()

  if (!desmosActStatus) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          {t('page.liveConsultation.payment.desmos.processing', { defaultValue: 'Traitement Desmos en cours…' })}
        </Typography>
      </Stack>
    )
  }

  const acts = desmosActStatus.acts || []
  const failedActs = acts.filter((a) => !a.success)
  const allSuccess = desmosActStatus.success && failedActs.length === 0

  return (
    <Stack spacing={1.5}>
      {acts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('page.consultation.paymentDialog.noDesmoActs', { defaultValue: 'Aucun acte Desmos.' })}
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {acts.map((act, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={1}>
              {act.success ? (
                <CheckCircleIcon color="success" fontSize="small" />
              ) : (
                <ErrorIcon color="error" fontSize="small" />
              )}
              <Typography variant="body2">{act.libelle}</Typography>
              {act.role && (
                <Chip
                  label={t(`page.liveConsultation.payment.desmos.role.${act.role}`)}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              {act.error && (
                <Typography variant="caption" color="error">
                  — {act.error}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      {allSuccess && acts.length > 0 && (
        <Alert severity="success">
          <Typography variant="body2">
            {t('page.liveConsultation.payment.desmos.successMessage', {
              defaultValue: 'Les actes ont été envoyés à Desmos avec succès. Vous pouvez accéder au logiciel Desmos pour finaliser la facturation.',
            })}
          </Typography>
        </Alert>
      )}

      {!allSuccess && acts.length > 0 && (
        <Alert severity="error">
          <Typography variant="body2">
            {t('page.liveConsultation.payment.desmos.errorMessage', {
              defaultValue: 'Certains actes n\'ont pas pu être envoyés à Desmos. Consultez le détail pour les créer manuellement.',
            })}
          </Typography>
        </Alert>
      )}
    </Stack>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Billing review section — user-driven billing status + issue reporting
// ─────────────────────────────────────────────────────────────────────────────

function BillingReviewStatusChip({ status }: { status?: BillingReviewStatus }) {
  const { t } = useTranslation()
  const current = status ?? BillingReviewStatus.Pending

  if (current === BillingReviewStatus.Completed) {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label={t('page.consultation.paymentDialog.billingReview.status.completed', { defaultValue: 'Terminé' })}
        color="success"
        size="small"
      />
    )
  }
  if (current === BillingReviewStatus.Issue) {
    return (
      <Chip
        icon={<WarningAmberIcon />}
        label={t('page.consultation.paymentDialog.billingReview.status.issue', { defaultValue: 'Problème' })}
        color="error"
        size="small"
      />
    )
  }
  return (
    <Chip
      icon={<HourglassEmptyIcon />}
      label={t('page.consultation.paymentDialog.billingReview.status.pending', { defaultValue: 'En attente' })}
      color="default"
      size="small"
      variant="outlined"
    />
  )
}

function JustificationsBlock({
  linkedFiles,
  linkableFiles,
  isUploading,
  onLink,
  onUnlink,
  onUpload,
}: {
  linkedFiles: Document[]
  linkableFiles: Document[]
  isUploading: boolean
  onLink: (key: string) => void
  onUnlink: (key: string) => void
  onUpload: (file: File) => void
}) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pickerAnchorEl, setPickerAnchorEl] = useState<HTMLElement | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) onUpload(f)
    e.target.value = ''
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
        {t('page.consultation.paymentDialog.billingReview.justifications.title', {
          defaultValue: 'Justificatifs',
        })}
      </Typography>
      {linkedFiles.length > 0 ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 1, rowGap: 0.75 }}>
          {linkedFiles.map((f) => (
            <Chip
              key={f.key}
              size="small"
              variant="outlined"
              label={f.filename}
              onClick={f.url ? () => window.open(f.url, '_blank', 'noopener,noreferrer') : undefined}
              onDelete={() => onUnlink(f.key)}
              deleteIcon={<CloseIcon />}
            />
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('page.consultation.paymentDialog.billingReview.justifications.empty', {
            defaultValue: 'No supporting document linked (CMU, certificate, etc.)',
          })}
        </Typography>
      )}

      <Stack direction="row" spacing={1}>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileChange}
        />
        <LoadingButton
          size="small"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          loading={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {t('page.consultation.paymentDialog.billingReview.justifications.upload', {
            defaultValue: 'Upload',
          })}
        </LoadingButton>
        {linkableFiles.length > 0 && (
          <>
            <Button
              size="small"
              variant="text"
              onClick={(e) => setPickerAnchorEl(e.currentTarget)}
            >
              {t('page.consultation.paymentDialog.billingReview.justifications.linkExisting', {
                defaultValue: 'Lier un fichier existant',
              })}
            </Button>
            <Menu
              anchorEl={pickerAnchorEl}
              open={Boolean(pickerAnchorEl)}
              onClose={() => setPickerAnchorEl(null)}
            >
              {linkableFiles.map((f) => (
                <MenuItem
                  key={f.key}
                  onClick={() => {
                    onLink(f.key)
                    setPickerAnchorEl(null)
                  }}
                >
                  {f.filename}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Stack>
    </Box>
  )
}

function BillingReviewSection({
  consultationId,
  consultation,
  onCompleted,
}: {
  consultationId: string
  consultation?: Consultation
  onCompleted?: () => void
}) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [updateBillingReview, { isLoading }] = useUpdateBillingReviewMutation()

  const currentStatus = consultation?.billingReview?.status ?? BillingReviewStatus.Pending
  const [isReportingIssue, setIsReportingIssue] = useState(currentStatus === BillingReviewStatus.Issue)
  const [issueReason, setIssueReason] = useState<BillingIssueReason | ''>(
    consultation?.billingReview?.issueReason ?? ''
  )
  const [note, setNote] = useState<string>(consultation?.billingReview?.note ?? '')
  const justificationFileKeys = consultation?.billingReview?.justificationFileKeys ?? []

  // Re-sync state when consultation updates from cache
  useEffect(() => {
    setIsReportingIssue(currentStatus === BillingReviewStatus.Issue)
    setIssueReason(consultation?.billingReview?.issueReason ?? '')
    setNote(consultation?.billingReview?.note ?? '')
  }, [consultation?.billingReview?.status, consultation?.billingReview?.issueReason, consultation?.billingReview?.note, currentStatus])

  const save = async (
    status: BillingReviewStatus,
    reason?: BillingIssueReason,
    text?: string,
    nextJustificationKeys?: string[],
  ) => {
    try {
      await updateBillingReview({
        id: consultationId,
        changes: {
          status,
          ...(status === BillingReviewStatus.Issue && reason ? { issueReason: reason } : {}),
          ...(text ? { note: text } : {}),
          ...(nextJustificationKeys ? { justificationFileKeys: nextJustificationKeys } : {}),
        },
      }).unwrap()
      enqueueSnackbar(
        t('page.consultation.paymentDialog.billingReview.saveSuccess', {
          defaultValue: 'Statut de facturation enregistré',
        }),
        { variant: 'success' }
      )
      if (status === BillingReviewStatus.Completed) {
        onCompleted?.()
      }
    } catch {
      enqueueSnackbar(
        t('page.consultation.paymentDialog.billingReview.saveError', {
          defaultValue: 'Erreur lors de l\'enregistrement du statut',
        }),
        { variant: 'error' }
      )
    }
  }

  const handleMarkCompleted = () => save(BillingReviewStatus.Completed)
  const handleSubmitIssue = () => {
    if (!issueReason) return
    save(BillingReviewStatus.Issue, issueReason as BillingIssueReason, note || undefined)
  }
  const handleReopen = () => {
    setIsReportingIssue(false)
    setIssueReason('')
    setNote('')
    save(BillingReviewStatus.Pending)
  }

  const [addFile, { isLoading: isUploading }] = useAddFileMutation()

  const persistJustifications = async (nextKeys: string[]) => {
    await save(
      currentStatus,
      currentStatus === BillingReviewStatus.Issue ? (issueReason as BillingIssueReason) || undefined : undefined,
      note || undefined,
      nextKeys,
    )
  }

  const handleUploadJustification = async (file: File) => {
    try {
      const uploaded = await addFile({
        file,
        objectType: objectTypes.CONSULTATION,
        objectId: consultationId,
        isBillingJustification: true,
      }).unwrap()
      await persistJustifications([...justificationFileKeys, uploaded.key])
    } catch {
      enqueueSnackbar(
        t('page.consultation.paymentDialog.billingReview.uploadError', {
          defaultValue: 'Failed to upload the supporting document',
        }),
        { variant: 'error' },
      )
    }
  }

  const handleLinkExisting = (key: string) => {
    if (justificationFileKeys.includes(key)) return
    persistJustifications([...justificationFileKeys, key])
  }

  const handleUnlinkJustification = (key: string) => {
    persistJustifications(justificationFileKeys.filter((k) => k !== key))
  }

  const linkedFiles = (consultation?.files ?? []).filter((f) => justificationFileKeys.includes(f.key))
  const linkableFiles = (consultation?.files ?? []).filter(
    (f) => !justificationFileKeys.includes(f.key) && !f.isRecording,
  )

  const reasonOptions = Object.values(BillingIssueReason).map((r) => ({
    value: r,
    label: t(`page.consultation.paymentDialog.billingReview.reasons.${r}`),
  }))

  return (
    <Box>
      {currentStatus === BillingReviewStatus.Completed && consultation?.billingReview?.reviewedAt && (
        <Alert severity="success" sx={{ mb: 1 }}>
          <Typography variant="body2">
            {t('page.consultation.paymentDialog.billingReview.completedAt', {
              defaultValue: 'Facturation marquée comme terminée le {{date}}',
              date: new Date(consultation.billingReview.reviewedAt).toLocaleString(),
            })}
          </Typography>
        </Alert>
      )}

      {currentStatus === BillingReviewStatus.Issue && consultation?.billingReview?.issueReason && !isReportingIssue && (
        <Alert severity="error" sx={{ mb: 1 }}>
          <AlertTitle>
            {t(`page.consultation.paymentDialog.billingReview.reasons.${consultation.billingReview.issueReason}`)}
          </AlertTitle>
          {consultation.billingReview.note && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {consultation.billingReview.note}
            </Typography>
          )}
        </Alert>
      )}

      <JustificationsBlock
        linkedFiles={linkedFiles}
        linkableFiles={linkableFiles}
        isUploading={isUploading}
        onLink={handleLinkExisting}
        onUnlink={handleUnlinkJustification}
        onUpload={handleUploadJustification}
      />

      {!isReportingIssue ? (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {currentStatus !== BillingReviewStatus.Completed && (
            <LoadingButton
              variant="contained"
              color="success"
              size="small"
              loading={isLoading}
              onClick={handleMarkCompleted}
              startIcon={<CheckCircleIcon />}
            >
              {t('page.consultation.paymentDialog.billingReview.markCompleted', {
                defaultValue: 'Facturation terminée',
              })}
            </LoadingButton>
          )}
          {currentStatus !== BillingReviewStatus.Issue && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => setIsReportingIssue(true)}
              startIcon={<WarningAmberIcon />}
            >
              {t('page.consultation.paymentDialog.billingReview.reportIssue', {
                defaultValue: 'Signaler un problème',
              })}
            </Button>
          )}
          {currentStatus !== BillingReviewStatus.Pending && (
            <Button variant="text" size="small" onClick={handleReopen} disabled={isLoading}>
              {t('page.consultation.paymentDialog.billingReview.reopen', {
                defaultValue: 'Rouvrir',
              })}
            </Button>
          )}
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <TextField
            select
            fullWidth
            size="small"
            label={t('page.consultation.paymentDialog.billingReview.reasonLabel', {
              defaultValue: 'Raison du problème',
            })}
            value={issueReason}
            onChange={(e) => setIssueReason(e.target.value as BillingIssueReason)}
          >
            {reasonOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label={t('page.consultation.paymentDialog.billingReview.noteLabel', {
              defaultValue: 'Note (optionnel)',
            })}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setIsReportingIssue(false)
                setIssueReason(consultation?.billingReview?.issueReason ?? '')
                setNote(consultation?.billingReview?.note ?? '')
              }}
            >
              {t('common:button.cancel.label')}
            </Button>
            <LoadingButton
              variant="contained"
              color="error"
              size="small"
              loading={isLoading}
              onClick={handleSubmitIssue}
              disabled={!issueReason}
            >
              {t('page.consultation.paymentDialog.billingReview.submitIssue', {
                defaultValue: 'Enregistrer le problème',
              })}
            </LoadingButton>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Manual payment form
// ─────────────────────────────────────────────────────────────────────────────

const manualPaymentSchema = yup.object({
  fullyCovered: yup.boolean().default(false),
  price: yup
    .number()
    .typeError('')
    .required()
    .min(0)
    .when('fullyCovered', {
      is: true,
      then: (schema) => schema,
      otherwise: (schema) => schema.moreThan(0),
    }),
  paymentType: yup.string().required().oneOf(Object.values(PaymentType)),
})

type ManualPaymentFormValues = yup.InferType<typeof manualPaymentSchema>

function ManualPaymentForm({
  consultationId,
  consultation,
  hasDesmos,
}: {
  consultationId: string
  consultation?: Consultation
  hasDesmos: boolean
}) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const [updateConsultation, { isLoading }] = useUpdateConsultationMutation()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid, isDirty },
  } = useForm<ManualPaymentFormValues>({
    resolver: yupResolver(manualPaymentSchema),
    mode: 'onChange',
    defaultValues: {
      fullyCovered: consultation?.price === 0,
      price: consultation?.price ?? 0,
      paymentType: consultation?.paymentType ?? PaymentType.Cash,
    },
  })

  // Keep form in sync when consultation reloads
  useEffect(() => {
    reset({
      fullyCovered: consultation?.price === 0,
      price: consultation?.price ?? 0,
      paymentType: consultation?.paymentType ?? PaymentType.Cash,
    })
  }, [consultation?.price, consultation?.paymentType, reset])

  // Force price to 0 when fully-covered toggled on
  const fullyCovered = useWatch({ control, name: 'fullyCovered' })
  useEffect(() => {
    if (fullyCovered) setValue('price', 0, { shouldValidate: true, shouldDirty: true })
  }, [fullyCovered, setValue])

  const paymentTypeOptions = useMemo(
    () =>
      Object.values(PaymentType).map((type) => ({
        label: t(`page.liveConsultation.payment.type.${type.toLowerCase()}`),
        value: type,
      })),
    [t]
  )

  const onSubmit = async (values: ManualPaymentFormValues) => {
    try {
      await updateConsultation({
        id: consultationId,
        changes: {
          price: values.price,
          paymentType: values.paymentType as PaymentType,
        },
      }).unwrap()
      enqueueSnackbar(t('notification.consultation.validate.success'), { variant: 'success' })
    } catch {
      enqueueSnackbar(t('notification.consultation.validate.error'), { variant: 'error' })
    }
  }

  const alreadyRecorded = consultation?.paid && !isDirty

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <Alert severity="info" icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
          <Typography variant="body2">
            {hasDesmos
              ? t('page.consultation.paymentDialog.manualPayment.instructions', {
                  defaultValue: 'Facturez le patient sur Desmos, puis indiquez ici le montant encaissé.',
                })
              : t('page.consultation.paymentDialog.manualPayment.instructionsNoDesmos', {
                  defaultValue: 'Enter the amount received for this consultation here.',
                })}
          </Typography>
        </Alert>

        {alreadyRecorded && (
          <Alert severity="success" icon={<CheckCircleIcon />}>
            <Typography variant="body2">
              {t('page.consultation.paymentDialog.manualPayment.alreadyRecorded', {
                defaultValue: 'Paiement déjà enregistré. Modifiez les champs ci-dessous si besoin.',
              })}
            </Typography>
          </Alert>
        )}

        <TextInputController
          control={control}
          name="price"
          label={t('page.liveConsultation.payment.consultationPrice.label')}
          type="number"
          fullWidth
          disabled={fullyCovered}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="body2" color="text.secondary">
                  €
                </Typography>
              </InputAdornment>
            ),
          }}
        />

        <CheckboxInputController
          control={control}
          name="fullyCovered"
          label={t('page.consultation.paymentDialog.manualPayment.fullyCovered', {
            defaultValue: 'Patient en prise en charge à 100 % (ALD, AME, CSS, maternité, AT/MP, etc.)',
          })}
        />

        <SelectInputController
          control={control}
          name="paymentType"
          label={t('page.liveConsultation.payment.type.title')}
          options={paymentTypeOptions}
          fullWidth
        />

        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          {!isDirty && !alreadyRecorded && (
            <Typography variant="caption" color="text.secondary">
              {t('page.consultation.paymentDialog.manualPayment.noChanges', {
                defaultValue: 'Modifiez le montant ou le type pour enregistrer',
              })}
            </Typography>
          )}
          <LoadingButton
            type="submit"
            variant="contained"
            size="small"
            loading={isLoading}
            disabled={!isValid || !isDirty}
          >
            {t('page.consultation.paymentDialog.manualPayment.submit', {
              defaultValue: 'Enregistrer le paiement',
            })}
          </LoadingButton>
        </Stack>
      </Stack>
    </form>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Dialog component
// ─────────────────────────────────────────────────────────────────────────────

export interface ConsultationPaymentDialogProps {
  open: boolean
  consultationId: string | null
  onClose: () => void
  /** Full consultation object from Redux / RTK Query cache */
  consultation?: Consultation
}

export function ConsultationPaymentDialog({
  open,
  consultationId,
  onClose,
  consultation,
}: ConsultationPaymentDialogProps) {
  const { t } = useTranslation()
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fspFile = useMemo(() => findFspFile(consultation?.files), [consultation?.files])

  const handleOpenFsp = () => {
    if (!fspFile?.url) return
    window.open(fspFile.url, '_blank', 'noopener,noreferrer')
  }

  // Auto-close after billing review is marked completed
  const handleBillingCompleted = () => {
    autoCloseTimer.current = setTimeout(() => {
      onClose()
    }, 2000)
  }

  // Clean up timer on unmount or manual close
  useEffect(() => {
    return () => {
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current)
    }
  }, [])

  const billingStatus = consultation?.billingReview?.status ?? BillingReviewStatus.Pending
  const isAllDone = billingStatus === BillingReviewStatus.Completed
  // Desmos is "in play" only when the backend ran the integration for this
  // consultation (status object is populated). When desmos isn't configured
  // for the establishment/waiting room, desmosActStatus stays undefined and
  // the related UI must stay hidden — otherwise we mislead users into
  // thinking processing is pending or that they should check Desmos.
  const hasDesmos = !!consultation?.desmosActStatus
  // Step numbering is dynamic so the visible flow stays 1, 2, 3 regardless
  // of whether the Desmos section is present.
  let stepCounter = 0
  const desmosStep = hasDesmos ? ++stepCounter : null
  const paymentStep = ++stepCounter
  const billingReviewStep = ++stepCounter

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography component="span" variant="h6" fontWeight={600} sx={{ flex: 1 }}>
            {t('page.consultation.paymentDialog.title', { defaultValue: 'Facturation — Fin de consultation' })}
          </Typography>
          {hasDesmos && <DesmosStatusChip desmosActStatus={consultation?.desmosActStatus} />}
        </Stack>
      </DialogTitle>

      <DialogContent>
        {/* Intro — tell the user what's expected */}
        <Alert severity={isAllDone ? 'success' : 'info'} sx={{ mb: 3 }} icon={isAllDone ? <CheckCircleIcon /> : undefined}>
          <Typography variant="body2">
            {isAllDone
              ? t('page.consultation.paymentDialog.allDone', {
                  defaultValue: 'La facturation de cette consultation est terminée. Vous pouvez fermer cette fenêtre.',
                })
              : hasDesmos
                ? t('page.consultation.paymentDialog.intro', {
                    defaultValue: 'Vérifiez les actes envoyés à Desmos, enregistrez le paiement si nécessaire, puis confirmez que la facturation est complète.',
                  })
                : t('page.consultation.paymentDialog.introNoDesmos', {
                    defaultValue: 'Record the payment if necessary, then confirm that billing is complete.',
                  })}
          </Typography>
        </Alert>

        <Stack spacing={3}>
          {/* Section — Desmos acts (only when desmos is configured) */}
          {hasDesmos && desmosStep && (
            <>
              <Box>
                <SectionHeader
                  step={desmosStep}
                  title={t('page.consultation.paymentDialog.desmosActs', { defaultValue: 'Vérifier les actes Desmos' })}
                  status={<DesmosStatusChip desmosActStatus={consultation?.desmosActStatus} />}
                />
                <Box sx={{ pl: 5 }}>
                  <DesmosActStatusSection desmosActStatus={consultation?.desmosActStatus} />
                </Box>
              </Box>
              <Divider />
            </>
          )}

          {/* FSP quick-open — independent of desmos, surfaces whenever an FSP file exists */}
          {fspFile && (
            <Alert
              severity="info"
              icon={<PrintIcon />}
              action={
                <Button
                  size="small"
                  variant="contained"
                  endIcon={<OpenInNewIcon />}
                  onClick={handleOpenFsp}
                >
                  {t('page.consultation.paymentDialog.openFsp', { defaultValue: 'Ouvrir la FSP' })}
                </Button>
              }
            >
              <AlertTitle sx={{ mb: 0 }}>
                {t('page.consultation.paymentDialog.fspReady', {
                  defaultValue: 'Feuille de soins prête à imprimer',
                })}
              </AlertTitle>
            </Alert>
          )}

          {/* Section — Payment */}
          <Box>
            <SectionHeader
              step={paymentStep}
              title={t('page.consultation.paymentDialog.payment', { defaultValue: 'Enregistrer le paiement' })}
              status={consultation?.paid ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label={t('page.liveConsultation.payment.title_paid', { defaultValue: 'Payé' })}
                  color="success"
                  size="small"
                />
              ) : undefined}
            />
            <Box sx={{ pl: 5 }}>
              {consultationId && (
                <ManualPaymentForm consultationId={consultationId} consultation={consultation} hasDesmos={hasDesmos} />
              )}
            </Box>
          </Box>

          <Divider />

          {/* Section — Billing review */}
          <Box>
            <SectionHeader
              step={billingReviewStep}
              title={t('page.consultation.paymentDialog.billingReview.title', { defaultValue: 'Confirmer la facturation' })}
              status={<BillingReviewStatusChip status={billingStatus} />}
            />
            <Box sx={{ pl: 5 }}>
              {consultationId && (
                <BillingReviewSection
                  consultationId={consultationId}
                  consultation={consultation}
                  onCompleted={handleBillingCompleted}
                />
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color={isAllDone ? 'success' : 'primary'}>
          {isAllDone
            ? t('page.consultation.paymentDialog.done', { defaultValue: 'Terminé — Fermer' })
            : t('common:button.close.label', { defaultValue: 'Fermer' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ConsultationPaymentDialog
